import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { koraxTokens, getKoraxModeColors } from "./designSystem";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";
import { MetaSdkProvider } from "./meta";

const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage = localStorage.getItem("primaryColorLight") || localStorage.getItem("primaryColorDark") || "#065183";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");
  const [primaryColorLight, setPrimaryColorLight] = useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] = useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () => {
      const darkPalette = {
        page: "#0b1220",
        surface: "#111b2e",
        elevated: "#17233a",
        border: "rgba(148, 163, 184, 0.2)",
        textPrimary: "#e6edf8",
        textSecondary: "#a9b6cc",
        topbar: "#12213a",
        tabHeader: "#172740",
        input: "#131f34",
      };

      const modeColors = getKoraxModeColors(mode);

      const resolvedPrimary =
        mode === "light"
          ? primaryColorLight || koraxTokens.colors.primary
          : primaryColorDark || koraxTokens.colors.secondary;

      return createTheme(
        {
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
              backgroundColor: resolvedPrimary,
            },
          },
          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "#F3F3F3" : "#333333",
            },
          },
          typography: {
            fontFamily: koraxTokens.typography.fontFamily,
            fontSize: 14,

            h1: {
              fontFamily: koraxTokens.typography.fontFamily,
              fontWeight: 700,
              letterSpacing: "-0.03em",
            },

            h2: {
              fontFamily: koraxTokens.typography.fontFamily,
              fontWeight: 700,
              letterSpacing: "-0.025em",
            },

            h3: {
              fontFamily: koraxTokens.typography.fontFamily,
              fontWeight: 700,
              letterSpacing: "-0.02em",
            },

            h4: {
              fontFamily: koraxTokens.typography.fontFamily,
              fontWeight: 700,
              letterSpacing: "-0.02em",
            },

            h5: {
              fontFamily: koraxTokens.typography.fontFamily,
              fontWeight: 700,
              letterSpacing: "-0.015em",
            },

            h6: {
              fontFamily: koraxTokens.typography.fontFamily,
              fontWeight: 700,
            },

            button: {
              fontFamily: koraxTokens.typography.fontFamily,
              fontWeight: 600,
              textTransform: "none",
              letterSpacing: 0,
            },

            body1: {
              fontFamily: koraxTokens.typography.fontFamily,
            },

            body2: {
              fontFamily: koraxTokens.typography.fontFamily,
            },

            caption: {
              fontFamily: koraxTokens.typography.fontFamily,
            },
          },

          shape: {
            borderRadius: koraxTokens.radius.md,
          },
          palette: {
            type: mode,
            primary: {
              main: resolvedPrimary,
              dark:
                mode === "light"
                  ? koraxTokens.colors.primaryHover
                  : "#0284C7",
              light:
                mode === "light"
                  ? koraxTokens.colors.primarySoft
                  : "#38BDF8",
              contrastText: "#FFFFFF",
            },
            textPrimary: resolvedPrimary,
            borderPrimary: resolvedPrimary,
            dark: { main: mode === "light" ? "#333333" : darkPalette.textPrimary },
            light: { main: mode === "light" ? "#F3F3F3" : darkPalette.elevated },
            fontColor: resolvedPrimary,
            tabHeaderBackground: mode === "light" ? "#EEE" : darkPalette.tabHeader,
            optionsBackground: mode === "light" ? "#fafafa" : darkPalette.surface,
            fancyBackground: mode === "light" ? "#fafafa" : darkPalette.page,
            total: mode === "light" ? "#fff" : darkPalette.surface,
            messageIcons: mode === "light" ? "grey" : "#F3F3F3",
            inputBackground: mode === "light" ? "#FFFFFF" : darkPalette.input,
            barraSuperior: mode === "light" ? primaryColorLight : darkPalette.topbar,
            background: {
              default: modeColors.background,
              paper: modeColors.surface,
            },
            text: {
              primary: modeColors.textPrimary,
              secondary: modeColors.textSecondary,
              disabled:
                mode === "light"
                  ? koraxTokens.colors.textDisabled
                  : "#64748B",
            },
            divider: modeColors.border,
            action: {
              hover: mode === "light" ? "rgba(15, 23, 42, 0.04)" : "rgba(148, 163, 184, 0.12)",
              selected: mode === "light" ? "rgba(37, 99, 235, 0.08)" : "rgba(56, 189, 248, 0.18)",
            },
          },

          overrides: {
            MuiCssBaseline: {
              "@global": {
                html: {
                  backgroundColor: modeColors.background,
                },

                body: {
                  backgroundColor: modeColors.background,
                  color: modeColors.textPrimary,
                  fontFamily: koraxTokens.typography.fontFamily,
                },

                "#root": {
                  backgroundColor: modeColors.background,
                },

                "*": {
                  boxSizing: "border-box",
                },
              },
            },

            MuiButton: {
              root: {
                minHeight: 40,
                padding: "8px 16px",
                borderRadius: koraxTokens.radius.md,
                fontWeight: 600,
                textTransform: "none",
                letterSpacing: 0,
                boxShadow: "none",
                transition: koraxTokens.transitions.normal,

                "&:hover": {
                  boxShadow: "none",
                  transform: "translateY(-1px)",
                },

                "&:active": {
                  transform: "translateY(0)",
                },

                "&$disabled": {
                  transform: "none",
                },
              },

              contained: {
                boxShadow: koraxTokens.shadows.xs,

                "&:hover": {
                  boxShadow: koraxTokens.shadows.sm,
                },
              },

              containedPrimary: {
                color: "#FFFFFF",
                backgroundColor: resolvedPrimary,

                "&:hover": {
                  backgroundColor:
                    mode === "light"
                      ? koraxTokens.colors.primaryHover
                      : "#0284C7",
                },
              },

              outlined: {
                borderColor: modeColors.border,
              },

              sizeSmall: {
                minHeight: 34,
                padding: "6px 12px",
                borderRadius: koraxTokens.radius.sm,
              },

              sizeLarge: {
                minHeight: 46,
                padding: "10px 20px",
                borderRadius: koraxTokens.radius.lg,
              },
            },

            MuiIconButton: {
              root: {
                borderRadius: koraxTokens.radius.md,
                transition: koraxTokens.transitions.normal,

                "&:hover": {
                  backgroundColor:
                    mode === "light"
                      ? "rgba(21, 94, 239, 0.07)"
                      : "rgba(56, 189, 248, 0.10)",
                },
              },
            },

            MuiPaper: {
              root: {
                backgroundImage: "none",
              },

              rounded: {
                borderRadius: koraxTokens.radius.lg,
              },

              elevation1: {
                boxShadow: koraxTokens.shadows.sm,
              },

              elevation2: {
                boxShadow: koraxTokens.shadows.md,
              },

              elevation3: {
                boxShadow: koraxTokens.shadows.lg,
              },
            },

            MuiCard: {
              root: {
                borderRadius: koraxTokens.radius.xl,
                border: `1px solid ${modeColors.border}`,
                boxShadow: koraxTokens.shadows.sm,
                backgroundColor: modeColors.surface,
              },
            },

            MuiCardContent: {
              root: {
                padding: koraxTokens.spacing.lg,

                "&:last-child": {
                  paddingBottom: koraxTokens.spacing.lg,
                },
              },
            },

            MuiOutlinedInput: {
              root: {
                minHeight: 40,
                borderRadius: koraxTokens.radius.md,
                backgroundColor:
                  mode === "light"
                    ? koraxTokens.colors.surface
                    : darkPalette.input,
                transition: koraxTokens.transitions.normal,

                "&:hover $notchedOutline": {
                  borderColor:
                    mode === "light"
                      ? koraxTokens.colors.borderStrong
                      : "#64748B",
                },

                "&$focused": {
                  boxShadow: koraxTokens.shadows.focus,
                },

                "&$focused $notchedOutline": {
                  borderWidth: 1,
                  borderColor: resolvedPrimary,
                },

                "&$disabled": {
                  backgroundColor:
                    mode === "light"
                      ? koraxTokens.colors.surfaceMuted
                      : "#1E293B",
                },
              },

              input: {
                padding: "10px 12px",
                fontSize: 14,
              },

              inputMultiline: {
                padding: 0,
              },

              notchedOutline: {
                borderColor: modeColors.border,
              },
            },

            MuiInputLabel: {
              root: {
                color: modeColors.textSecondary,
                fontSize: 14,

                "&$focused": {
                  color: resolvedPrimary,
                },
              },
            },

            MuiFormLabel: {
              root: {
                color: modeColors.textSecondary,

                "&$focused": {
                  color: resolvedPrimary,
                },
              },
            },

            MuiFormHelperText: {
              root: {
                marginLeft: 2,
                fontSize: 12,
              },
            },

            MuiSelect: {
              select: {
                "&:focus": {
                  backgroundColor: "transparent",
                },
              },
            },

            MuiDialog: {
              paper: {
                borderRadius: koraxTokens.radius.xl,
                border: `1px solid ${modeColors.border}`,
                boxShadow: koraxTokens.shadows.lg,
              },
            },

            MuiDialogTitle: {
              root: {
                padding: "20px 24px 12px",

                "& h2": {
                  color: modeColors.textPrimary,
                  fontSize: 19,
                  fontWeight: 700,
                  letterSpacing: "-0.015em",
                },
              },
            },

            MuiDialogContent: {
              root: {
                padding: "12px 24px 20px",
              },
            },

            MuiDialogActions: {
              root: {
                gap: koraxTokens.spacing.xs,
                padding: "12px 24px 20px",
              },
            },

            MuiChip: {
              root: {
                height: 28,
                borderRadius: koraxTokens.radius.pill,
                fontWeight: 600,
                fontSize: 12,
              },

              sizeSmall: {
                height: 24,
              },

              outlined: {
                borderColor: modeColors.border,
              },
            },

            MuiTooltip: {
              tooltip: {
                padding: "7px 10px",
                borderRadius: koraxTokens.radius.sm,
                backgroundColor:
                  mode === "light"
                    ? koraxTokens.colors.sidebar
                    : "#020617",
                color: "#FFFFFF",
                fontSize: 12,
                fontWeight: 500,
                boxShadow: koraxTokens.shadows.md,
              },

              arrow: {
                color:
                  mode === "light"
                    ? koraxTokens.colors.sidebar
                    : "#020617",
              },
            },

            MuiTableCell: {
              root: {
                padding: "12px 16px",
                borderBottom: `1px solid ${modeColors.border}`,
                color: modeColors.textPrimary,
              },

              head: {
                color: modeColors.textSecondary,
                backgroundColor:
                  mode === "light"
                    ? koraxTokens.colors.surfaceSoft
                    : "#182230",
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              },
            },

            MuiTableRow: {
              root: {
                transition: koraxTokens.transitions.fast,

                "&:hover": {
                  backgroundColor:
                    mode === "light"
                      ? "rgba(21, 94, 239, 0.035)"
                      : "rgba(56, 189, 248, 0.05)",
                },
              },
            },

            MuiTabs: {
              root: {
                minHeight: 40,
              },

              indicator: {
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            },

            MuiTab: {
              root: {
                minHeight: 40,
                padding: "8px 14px",
                textTransform: "none",
                fontSize: 13,
                fontWeight: 600,

                "@media (min-width: 600px)": {
                  minWidth: 90,
                },
              },
            },

            MuiMenu: {
              paper: {
                marginTop: 6,
                borderRadius: koraxTokens.radius.lg,
                border: `1px solid ${modeColors.border}`,
                boxShadow: koraxTokens.shadows.lg,
              },
            },

            MuiMenuItem: {
              root: {
                minHeight: 38,
                margin: "2px 6px",
                padding: "7px 10px",
                borderRadius: koraxTokens.radius.sm,
                fontSize: 14,
              },
            },

            MuiDivider: {
              root: {
                backgroundColor: modeColors.border,
              },
            },

            MuiAvatar: {
              root: {
                fontFamily: koraxTokens.typography.fontFamily,
                fontWeight: 600,
              },
            },

            MuiSkeleton: {
              root: {
                borderRadius: koraxTokens.radius.sm,
              },
            },
          },

          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (appLogoDark === defaultLogoDark && appLogoLight !== defaultLogoLight) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (appLogoDark !== defaultLogoDark && appLogoLight === defaultLogoLight) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      );
    },
    [appLogoLight, appLogoDark, appLogoFavicon, appName, locale, mode, primaryColorDark, primaryColorLight]
  );

  useEffect(() => {
    const i18nlocale = localStorage.getItem("i18nextLng");
    const browserLocale = i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

    if (browserLocale === "ptBR") {
      setLocale(ptBR);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(color || koraxTokens.colors.primary);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(color || koraxTokens.colors.secondary);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        setAppLogoLight(file ? getBackendUrl() + "/public/" + file : defaultLogoLight);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        setAppLogoDark(file ? getBackendUrl() + "/public/" + file : defaultLogoDark);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        setAppLogoFavicon(file ? getBackendUrl() + "/public/" + file : defaultLogoFavicon);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appName")
      .then((name) => {
        setAppName(name || "Korax");
      })
      .catch((error) => {
        console.log("!==== Erro ao carregar temas: ====!", error);
        setAppName("Korax");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--primaryColor",
      mode === "light"
        ? primaryColorLight || koraxTokens.colors.primary
        : primaryColorDark || koraxTokens.colors.secondary
    );

    root.style.setProperty(
      "--appBackground",
      mode === "light"
        ? koraxTokens.colors.background
        : koraxTokens.colors.backgroundDark
    );

    root.style.setProperty(
      "--korax-primary",
      mode === "light"
        ? primaryColorLight || koraxTokens.colors.primary
        : primaryColorDark || koraxTokens.colors.secondary
    );
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon url={appLogoFavicon ? getBackendUrl() + "/public/" + appLogoFavicon : defaultLogoFavicon} />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
              <MetaSdkProvider>
                <div
                  style={{
                    position: "relative",
                    overflow: "visible",
                    zIndex: 0,
                    minHeight: "100vh"
                  }}
                >
                  <Routes />
                </div>
              </MetaSdkProvider>
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
